import React, { useEffect, useState } from 'react'
import Layout from '../../../components/Layout/Layout'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { Modal } from 'antd'
import { toast } from 'react-toastify'
import CreateNewStockCheck from './CreateNewStockCheck'
import 'bootstrap-icons/font/bootstrap-icons.min.css'

// recordDate is stored already shifted to local time, so read it back as UTC.
const formatDate = (value) => {
  const date = new Date(value)
  return isNaN(date) ? '' : date.toISOString().split('T')[0]
}

const AdminStockCheckListIndex = () => {
  const navigate = useNavigate()
  const [events, setEvents] = useState([])
  const [visibleAddEventModal, setVisibleAddEventModal] = useState(false)
  const [stockAddData, setStockAddData] = useState({
    eventTitle: '',
  })
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [deletingId, setDeletingId] = useState(null)

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const { data } = await axios.get('/api/v1/admin/stock-check/lists')
        setEvents(Array.isArray(data) ? data : [])
      } catch (error) {
        console.log('Failed to fetch lists:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchEvents()
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    setStockAddData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (submitting || !stockAddData.eventTitle.trim()) return
    setSubmitting(true)
    try {
      const { data } = await axios.post('/api/v1/admin/stock-check/list/add', {
        eventTitle: stockAddData.eventTitle,
      })
      closeModal()
      setEvents((prevEvents) => [data.data, ...prevEvents])
      toast.success('Stock check added')
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add stock check')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (event) => {
    const confirmed = window.confirm(
      `Delete "${event.event}"?\n\n` +
        'This will delete all related data and cannot be undone.'
    )
    if (!confirmed) return

    setDeletingId(event._id)
    try {
      await axios.delete(`/api/v1/admin/stock-check/list/${event._id}`)
      setEvents((prevEvents) =>
        prevEvents.filter((item) => item._id !== event._id)
      )
      toast.success('Stock check deleted')
    } catch (error) {
      toast.error(
        error.response?.data?.message || 'Failed to delete stock check'
      )
    } finally {
      setDeletingId(null)
    }
  }

  const handleViewDetails = (listId) => {
    navigate(`/admin/stock-check/details/${listId}`, {
      state: { id: listId },
    })
  }

  const openModal = () => {
    setVisibleAddEventModal(true)
  }

  const closeModal = () => {
    setVisibleAddEventModal(false)
    setStockAddData({ eventTitle: '' })
  }

  const updateEventStatus = (eventId, newStatus) => {
    setEvents((prevEvents) =>
      prevEvents.map((event) =>
        event._id === eventId ? { ...event, status: newStatus } : event
      )
    )
  }

  const handleStatusClick = async (eventId, currentStatus) => {
    const confirmed = window.confirm('Confirm update?')
    if (confirmed) {
      try {
        const newStatus = currentStatus === 1 ? 2 : 1
        const response = await axios.patch(
          `/api/v1/admin/stock-check/update-status/${eventId}`,
          { status: newStatus }
        )

        if (response.status === 200) {
          toast.success('Status updated successfully')
          updateEventStatus(eventId, newStatus)
        }
      } catch (error) {
        toast.error('Failed to update status')
      }
    }
  }

  return (
    <Layout title={'Stock Check List'}>
      <div className='container-fluid px-3 px-md-4 pt-3'>
        <div className='d-flex justify-content-between align-items-center mb-3'>
          <h1 className='h5 mb-0'>Stock Check List</h1>
          <button
            type='button'
            className='btn btn-info btn-sm'
            onClick={openModal}
            aria-label='Add New Stock Check'
            title='Add New Stock Check'
          >
            <i className='bi bi-plus-lg'></i>
          </button>
        </div>

        {loading ? (
          <div className='text-center text-muted py-5'>
            <div className='spinner-border' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
          </div>
        ) : events.length === 0 ? (
          <div className='text-center text-muted border rounded py-5'>
            No stock checks yet.
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table table-hover align-middle mb-0'>
              <thead className='table-light'>
                <tr>
                  <th scope='col'>Date</th>
                  <th scope='col'>Info</th>
                  <th scope='col'>Status</th>
                  <th scope='col'></th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event._id}>
                    <td className='text-nowrap'>
                      {formatDate(event.recordDate)}
                    </td>
                    <td>{event.event}</td>
                    <td>
                      <span
                        className='fw-bold'
                        style={{
                          color: event.status === 1 ? 'green' : 'grey',
                          cursor: 'pointer',
                        }}
                        onClick={() =>
                          handleStatusClick(event._id, event.status)
                        }
                        title='Click to toggle status'
                      >
                        {event.status === 1 ? 'Open' : 'Closed'}
                      </span>
                    </td>
                    <td className='text-end'>
                      <button
                        className='btn btn-success btn-sm'
                        onClick={() => handleViewDetails(event._id)}
                      >
                        View
                      </button>
                      <button
                        type='button'
                        className='btn btn-danger btn-sm ms-2'
                        onClick={() => handleDelete(event)}
                        disabled={deletingId === event._id}
                        aria-label={`Delete ${event.event}`}
                        title='Delete Stock Check'
                      >
                        {deletingId === event._id ? (
                          <span
                            className='spinner-border spinner-border-sm'
                            role='status'
                            aria-hidden='true'
                          ></span>
                        ) : (
                          <i className='bi bi-trash'></i>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Modal
        closable={false}
        onCancel={closeModal}
        footer={null}
        open={visibleAddEventModal}
      >
        <CreateNewStockCheck
          closeModal={closeModal}
          handleSubmit={handleSubmit}
          handleChange={handleChange}
          eventTitle={stockAddData.eventTitle}
          submitting={submitting}
        />
      </Modal>
    </Layout>
  )
}

export default AdminStockCheckListIndex
